import type { CanonicalSchema } from '#layers/data-model/types/schema/canonical-schema';
import type { WorkspaceRuntimeHandle } from '#layers/runtime/public';
import { defineStore } from 'pinia';
import { computed, readonly, ref } from 'vue';
import { useNuxtApp } from '#app/nuxt';
import { SCHEMA_OPERATION_TIMEOUT_MS } from '#layers/data-model/constants/fetch';
import { parseIntrospectDataSourceResponse } from '#layers/data-model/types/schema/canonical-schema';
import { useWorkspaceRuntimeHandle } from '#layers/runtime/public';
import { useToastResultHandling } from '#layers/ui/public';
import { useWorkspaceStore } from '#layers/workspace/public';

interface SourceCreateFlowRequest {
	controller: AbortController;
	handle: WorkspaceRuntimeHandle;
}

/**
 * Holds the introspected `CanonicalSchema`, its hash, and the assembled create body for the whole
 * "add data source" flow. The Nuxt UI stepper unmounts inactive steps, so this state cannot live
 * in the step components and belongs in a store instead.
 *
 * `introspect` takes two bodies. The first is the connection probe sent to
 * `sources/data/introspect` (the connector's transformed options merged with `provider`, matching
 * the sibling `sources/data/test` call in `Source/OptionsForm.vue`). The second is the create body
 * (`buildCreateSourceBody`), remembered on success so the Review step can submit the terminal
 * import with an added `introspection` block after the connect form has unmounted. Both are typed
 * `Record<string, unknown>` because connector options are dynamic per provider.
 */
export const useSourceCreateFlowStore = defineStore('source-create-flow', () => {
	const { t } = useNuxtApp().$i18n;

	const { onError: showIntrospectionError } = useToastResultHandling({
		error: {
			title: t('pages.data_model.introspection.error.title'),
		},
	});

	const createBody = ref<Record<string, unknown>>();
	const schema = ref<CanonicalSchema>();
	const hash = ref<string>();
	const isIntrospecting = ref(false);

	// True once the flow holds discardable state: an in-flight or completed introspection, or an
	// assembled create body. Both hosts read it to guard leaving or closing the flow mid-way.
	const hasStartedFlow = computed(() =>
		isIntrospecting.value || Boolean(schema.value) || Boolean(createBody.value));

	// Raw Step-1 form draft for the interactive stepper, tagged with the connector it was captured
	// for. Held here so it survives the stepper unmounting the inactive Connect step. The connector id
	// lets a remount tell a same-connector step-return (restore the whole draft) from a connector
	// switch (carry only the connection string). Cleared by reset() on a new visit.
	const draft = ref<{ connectorId: string; options: Record<string, unknown> }>();

	// Identifies the in-flight request so a superseded response is discarded instead of
	// overwriting newer state: this happens when the connection form is edited and re-introspected
	// before the first request resolves, or when `reset()` runs mid-flight. Mirrors the staleness
	// guard in `source-introspection.ts`.
	let activeRequest: SourceCreateFlowRequest | undefined;

	useWorkspaceStore().onLifecycleTransition(() => reset());

	async function introspect({ probeBody, createBody: nextCreateBody }: { probeBody: Record<string, unknown>; createBody: Record<string, unknown> }) {
		activeRequest?.controller.abort();

		const request: SourceCreateFlowRequest = {
			controller: new AbortController(),
			handle: useWorkspaceRuntimeHandle(),
		};
		activeRequest = request;

		// Invalidate any prior result before fetching. The create body is only replaced once the
		// fetch succeeds, so create body, schema, and hash always describe the same introspection.
		schema.value = undefined;
		hash.value = undefined;
		isIntrospecting.value = true;

		try {
			const { fetch } = request.handle;
			const response = await fetch('sources/data/introspect', {
				method: 'POST',
				timeout: SCHEMA_OPERATION_TIMEOUT_MS,
				body: probeBody,
				signal: request.controller.signal,
			});

			if (!canCommit(request)) return;

			const result = parseIntrospectDataSourceResponse(response);

			createBody.value = nextCreateBody;
			schema.value = result.schema;
			hash.value = result.hash;
		}
		catch (error_) {
			if (!canCommit(request)) return;

			showIntrospectionError(error_);
		}
		finally {
			if (activeRequest === request) {
				activeRequest = undefined;
				isIntrospecting.value = false;
			}
		}
	}

	function canCommit(request: SourceCreateFlowRequest) {
		return activeRequest === request
			&& !request.controller.signal.aborted
			&& !request.handle.signal.aborted;
	}

	function setDraft(connectorId: string, options: Record<string, unknown>) {
		draft.value = { connectorId, options };
	}

	function reset() {
		activeRequest?.controller.abort();
		activeRequest = undefined;
		createBody.value = undefined;
		schema.value = undefined;
		hash.value = undefined;
		isIntrospecting.value = false;
		draft.value = undefined;
	}

	return {
		createBody: readonly(createBody),
		schema: readonly(schema),
		hash: readonly(hash),
		isIntrospecting: readonly(isIntrospecting),
		hasStartedFlow,
		draft: readonly(draft),
		introspect,
		setDraft,
		reset,
	};
});
